/**
 * Centralized Permissions Configuration
 * Single Source of Truth for all permission-related constants
 *
 * Usage:
 * - Import `Permissions` enum for type-safe permission checks
 * - Import `PermissionValues` for runtime permission arrays
 * - Import `PermissionType` for TypeScript type annotations
 */

/**
 * All available permissions in the system
 * Use this enum in route definitions and permission checks
 */
export enum Permissions {
  // Admin permission (full access)
  ADMIN = "ADMIN",

  // Report read permissions
  ADS_PERFORMANCE = "report:read:ads-performance",
  ADS_SUMMARY = "report:read:ads-summary",
  BRAND_PROTECTION = "report:read:brand-protection",
  BUY_BOX_INTELLIGENCE = "report:read:buy-box-intelligence",
  RETURNS = "report:read:returns",
  REVIEW_REPORT = "report:read:reviews-report",
  SALES_REPORT = "report:read:sales-report",
  ADS_CAMPAIGN = "report:read:campaigns-report",
  INVENTORY = "report:read:inventory-report",
  ALERTS = "report:read:product-alerts",
  PERFORMANCE = "report:read:product-performance",
  ACCOUNT_HEALTH = "report:read:account-health",
  WAREHOUSE = "report:read:warehouse",
  CUSTOMER_ANALYTICS = "report:read:customer-analytics",
  ZINUS_CALCULATOR = "report:read:zinus",
  FUNNEL = "report:read:search-query-performance",
  PRODUCT_REVIEWS = "report:read:product-reviews",
  EMAILS = "report:read:email-management",
  ADVERTISING_ANALYTICS = "report:read:advertising-analytics",
  PROFIT_ANALYSIS = "report:read:profit-analysis",
  COMPETITOR = "report:read:competitor-report",
  CATEGORY_ANALYSIS = "report:read:category-reporting",
  INSIGHTFUL = "report:read:insightful",
  BUSINESS_REVIEW = "report:read:business-review",
  PRODUCT_TIERS = "report:read:product-tiers",
  HALO_EFFECT = "report:read:halo-effect",

  // Report edit permissions
  EDIT_ADS_CAMPAIGN = "report:edit:campaigns-report",

  // Report write permissions
  RESEND_MANAGER_EMAILS = "report:write:resend-manager-emails",
  SEND_CLIENT_EMAILS = "report:write:send-client-emails",

  // Settings write permissions — brand-scoped, not admin-only
  WRITE_BRAND_SETTINGS = "settings:write:brand-settings",

  // Vendor Central report read permissions (used in meta.vendorPermissions)
  READ_VENDOR_REPORT_SALES = "vendor-report:read:sales-report",
  READ_VENDOR_REPORT_ADS_SUMMARY = "vendor-report:read:ads-summary",
}

/**
 * TypeScript type for permission values
 * Use for type annotations where string permission is expected
 */
export type PermissionType = `${Permissions}`;

/**
 * Array of all permission values for runtime checks
 */
export const PermissionValues = Object.values(Permissions) as PermissionType[];

/**
 * Permission groups for common use cases
 */
export const PermissionGroups = {
  /** Permissions required for Insightful dashboard */
  INSIGHTFUL: [
    Permissions.SALES_REPORT,
    Permissions.ADS_SUMMARY,
    Permissions.REVIEW_REPORT,
    Permissions.BRAND_PROTECTION,
  ],

  /** All advertising-related permissions */
  ADVERTISING: [
    Permissions.FUNNEL,
    Permissions.ADS_SUMMARY,
    Permissions.ADVERTISING_ANALYTICS,
    Permissions.ADS_CAMPAIGN,
  ],

  CATEGORY: [
    Permissions.CATEGORY_ANALYSIS,
    Permissions.COMPETITOR,
  ],

  PRODUCT_REVIEWS: [
    Permissions.REVIEW_REPORT,
    Permissions.PRODUCT_REVIEWS,
  ],

  ACCOUNT_HEALTH: [
    Permissions.ACCOUNT_HEALTH,
    Permissions.ALERTS,
    Permissions.BRAND_PROTECTION,
    Permissions.BUY_BOX_INTELLIGENCE,
  ],

  COMMUNICATION: [
    Permissions.EMAILS,
    Permissions.SEND_CLIENT_EMAILS,
    Permissions.RESEND_MANAGER_EMAILS,
  ],

  ADMIN: [
    Permissions.ADMIN,
    Permissions.EMAILS,
    Permissions.SEND_CLIENT_EMAILS,
    Permissions.RESEND_MANAGER_EMAILS,
  ],

  /** All read permissions */
  ALL_READ: PermissionValues.filter(p => p.startsWith("report:read:")),

  /** All edit permissions */
  ALL_EDIT: PermissionValues.filter(p => p.startsWith("report:edit:")),
} as const;
